import { useEffect, useRef } from 'react';
import {
  BeePluginErrorCodes,
  Builder,
  useBuilder,
  type BeePluginError,
  type IEntityContentJson,
  type IToken,
} from '@beefree.io/react-email-builder';
import beefreeTopbarCss from './beefree-topbar.css?url';
import { MERGE_FIELDS } from '@/features/marketing-shared';
import type { BeefreeDocument } from '../model/beefree-document';

/**
 * O tipo `IBeeConfig` tem `sidebarPosition`, mas não tem flag para esconder a top bar.
 * Esconder a toolbar é opção de servidor ("Show toolbar"). No cliente, a doc manda `customCss`.
 */
function beefreeTopbarCssUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URL(beefreeTopbarCss, window.location.origin).href;
}

const CONTAINER_ID = 'fixe-beefree-editor';

function toSdkDocument(document: BeefreeDocument | null): IEntityContentJson {
  if (!document) return { comments: {}, page: {} as IEntityContentJson['page'] };
  return {
    comments: document.comments as IEntityContentJson['comments'],
    page: document.page as unknown as IEntityContentJson['page'],
  };
}

export interface BeefreeSavePayload {
  json: string;
  html: string;
}

export function readBeefreeSave(result: unknown): BeefreeSavePayload | null {
  if (!result || typeof result !== 'object') return null;
  const root = result as { data?: unknown; json?: unknown; html?: unknown };
  const data = root.data && typeof root.data === 'object' ? (root.data as { json?: unknown; html?: unknown }) : root;
  if (typeof data.json !== 'string' || !data.json.trim()) return null;
  return { json: data.json, html: typeof data.html === 'string' ? data.html : '' };
}

interface BeefreeEditorProps {
  uid: string;
  token: IToken;
  document: BeefreeDocument | null;
  onSave: (payload: BeefreeSavePayload) => void;
  onChangeJson: (json: string) => void;
  onRefreshToken: () => Promise<IToken | null>;
  onRemount: (json: string | null) => void;
  saveBridge: { current: (() => Promise<BeefreeSavePayload | null>) | null };
}

/**
 * Editor oficial do Beefree (`@beefree.io/react-email-builder`).
 * Imagem, botão e colunas ficam no SDK. O token chega do BFF.
 */
export function BeefreeEditor({
  uid,
  token,
  document,
  onSave,
  onChangeJson,
  onRefreshToken,
  onRemount,
  saveBridge,
}: BeefreeEditorProps) {
  const tokenRef = useRef(token);
  const onSaveRef = useRef(onSave);
  const onChangeRef = useRef(onChangeJson);
  const onRefreshRef = useRef(onRefreshToken);
  const onRemountRef = useRef(onRemount);
  onSaveRef.current = onSave;
  onChangeRef.current = onChangeJson;
  onRefreshRef.current = onRefreshToken;
  onRemountRef.current = onRemount;

  const topbarCss = beefreeTopbarCssUrl();
  const { id, save, updateToken } = useBuilder({
    container: CONTAINER_ID,
    uid,
    language: 'pt-BR',
    trackChanges: true,
    sidebarPosition: 'left',
    ...(topbarCss ? { customCss: topbarCss } : {}),
    specialLinks: [{ type: 'unsubscribe', label: 'Cancelar inscrição', link: '{{unsubscribe}}' }],
    mergeTags: MERGE_FIELDS.map((field) => ({
      name: field.label,
      value: field.token,
      previewValue: field.sample,
    })),
  });

  useEffect(() => {
    saveBridge.current = async () => {
      const result = await save();
      return readBeefreeSave(result);
    };
    return () => {
      saveBridge.current = null;
    };
  }, [save, saveBridge]);

  useEffect(() => {
    if (tokenRef.current.access_token === token.access_token) return;
    tokenRef.current = token;
    updateToken(token);
  }, [token, updateToken]);

  const handleError = (error: BeePluginError) => {
    if (error.code === BeePluginErrorCodes.EXPIRED_TOKEN_CANNOT_REFRESHED) {
      void onRefreshRef.current().then((next) => {
        if (next) updateToken(next);
      });
      return;
    }
    if (error.code === BeePluginErrorCodes.EXPIRED_TOKEN_MUST_REFRESHED) {
      const extra = error as BeePluginError & { template?: unknown };
      const template = typeof extra.template === 'string' ? extra.template : null;
      onRemountRef.current(template);
    }
  };

  return (
    <div className="h-full min-h-0 w-full min-w-0">
      <Builder
        id={id}
        token={token}
        template={toSdkDocument(document)}
        height="100%"
        width="100%"
        onSave={(jsonFile, htmlFile) => {
          if (typeof jsonFile !== 'string' || !jsonFile.trim()) return;
          onSaveRef.current({ json: jsonFile, html: typeof htmlFile === 'string' ? htmlFile : '' });
        }}
        onChange={(jsonFile) => {
          if (typeof jsonFile === 'string' && jsonFile.trim()) onChangeRef.current(jsonFile);
        }}
        onError={handleError}
      />
    </div>
  );
}
